import type { ClassDefinition } from '../../schemas/classDefinition';
import { paladinoAdvanced2to5Moves } from '../moves/paladino/advanced-2-5';
import { paladinoAdvanced6to10Moves } from '../moves/paladino/advanced-6-10';
import { paladinoStartingAutoMoves } from '../moves/paladino/initial';

/**
 * Definizione della classe **Paladino** (manuale V1, rr. 4780-5128).
 *
 * Nona classe trascritta. Pattern principali stress-testati:
 *
 * - **FI-1 chiuso strutturalmente**: prima applicazione di
 *   `Archetype.baseHpOverride`. L'archetipo **Nano** ha
 *   `baseHpOverride: 12` (manuale V1, r. 4846: «i tuoi PF massimi sono
 *   12+Costituzione»). Il calcolo derivato `hp.max` legge `baseHpOverride`
 *   se l'archetipo selezionato lo definisce.
 * - **Casteggiante condizionato a 2 livelli**:
 *    - *Ordine Sacro* + Tradizione (Negromanzia/Valore/Natura) → spell-list
 *      `paladino-native` attiva (pattern coerente con Chierico).
 *    - *Ordine Sovrannaturale* → description-only stub (Vincolo della
 *      Catena/Lama/Tomo del Warlock). Refactor strutturale tracciato
 *      come **FI-27**, da chiudere nello scope della PR Warlock (11/12).
 * - **Tradizione Natura → archetipo Elfo**: vincolo già modellato via
 *   `SpellAccess.requiresArchetype: 'elfo'` (FI-2 risolto).
 * - **Spell Paladino = spell Chierico** via estensione `availableTo`
 *   (no nuovi spell del Paladino): le tre Tradizioni del Paladino sono
 *   sottoinsieme stretto delle 5 del Chierico.
 *
 * **Punti notevoli**:
 * - Dado di danno **d10**, PF base 10 (12 per archetipo Nano), Carico base 9
 * - 5 archetipi razza-tematici
 * - 4 mosse iniziali automatiche + 2 mosse iniziali di scelta (`pickCount: 1`)
 * - 16 mosse avanzate L2-5, 14 mosse avanzate L6-10 (41 mosse totali)
 * - 3 template di Legami (= `maxBonds`, FI-3 rispettato; estremo basso del
 *   range, condiviso con Mago)
 * - **Casteggiante** (`spellcasting` definito con 3 Tradizioni)
 */
export const paladinoClassDefinition: ClassDefinition = {
  id: 'paladino',
  name: 'Paladino',

  intro:
    "L'Inferno li attende. Un'eternità di tormenti, tra fiamme inestinguibili, ghiacci implacabili o qualsiasi altra pena si addica ai peccati delle orde dannate di Dungeon World Rebrewed. Tu sei l'unico baluardo tra le loro anime e le oscure fosse della tortura… o forse sei il loro giudice, colui che pronuncia la sentenza.\n\nIl Chierico può invocare i numi che dimorano nei cieli. Il Guerriero può brandire la sua lama in nome di un ideale. Il Warlock deve scendere a patti col suo patrono per ottenere il potere. Ma tu... tu sei la manifestazione stessa della volontà di un'entità ultraterrena. Il tuo Ordine si è consacrato a essa, e solo voi conoscete la verità sul suo conto.\n\nTu sei gli occhi e le mani di *qualcosa* di più grande, che si manifesta nel mondo attraverso il tuo stesso essere. Che tu sia un guerriero della luce o un templare del terrore, un difensore delle foreste o un messaggero del caos, la tua forza è forgiata nella devozione. La tua armatura è un simbolo di un potere che trascende i mortali, la tua parola una legge da far rispettare.\n\nTu possiedi una purezza di intenti che i tuoi compagni non possono nemmeno immaginare. Anche se il tuo cammino ti obbliga a compiere scelte che metterebbero alla prova l'anima più salda, tu non vacilli e continui ad avanzare con la perseveranza e la sicurezza che ti contraddistinguono.\n\nErgiti, dunque, Paladino, tra questi mortali. Impugna le armi per la tua devozione e manifesta in questo mondo perduto e tormentato la volontà dell'entità a cui il tuo ordine si è consacrato.",

  // Manuale V1, rr. 4793-4799
  suggestedNames: [
    // Umano
    'Brianne',
    'Thaddeus',
    'Augustine',
    'Cassius',
    'Adrian',
    'Eva',
    'Octavia',
    'Regulus',
    'Hilda',
    // Elfo
    'Xaar',
    'Aelel',
    'Drieldir',
    'Lantariel',
    'Shie',
    'Suwen',
    'Le-tha',
    'Cecirir',
    'Alwe',
    'Alelen',
    'Leev',
    // Nano
    'Gudran',
    'Ogil',
    'Brutilde',
    'Moddur',
    'Freja',
    'Zandra',
    'Dina',
    'Ailin',
    'Afur',
    'Bidur',
    'Ila',
    // Halfling
    'Fredas',
    'Lonham',
    'Amamac',
    'Namond',
    'Gwen',
    'Ragilda',
    'Todonna',
    'Ardprim',
    // Mezz'orco
    'Gaal',
    'Bala',
    'Elsa',
    'Zorgmur',
    'Org',
    'Kruul',
    'Morer',
    'Elphia',
    'Daphne',
    'Hausa',
  ],

  suggestedTitles: [],

  // Manuale V1, rr. 4801-4806
  aspetti: [
    {
      label: 'Occhi',
      options: ['Gentili', 'Ardenti', 'Luminosi'],
    },
    {
      label: 'Capelli',
      options: ['Elmo', 'Capelli acconciati', 'Calvo'],
    },
    {
      label: 'Vesti',
      options: ['Simbolo sacro', 'Tatuaggi', "Divisa dell'Ordine"],
    },
    {
      label: 'Corpo',
      options: ['Atletico', 'Massiccio', 'Esile'],
    },
  ],

  // Manuale V1, rr. 4808-4814.
  // **NB FI-1**: il `baseHp` standard del Paladino è 10 (10 + COS). L'archetipo
  // Nano lo eleva a 12 (12 + COS) via `Archetype.baseHpOverride: 12`.
  damageDie: 'd10',
  baseHp: 10,
  baseLoad: 9,

  // Manuale V1, rr. 4816-4824
  alignmentPrecepts: {
    buono: 'Sfidi il pericolo per proteggere chi è più debole.',
    legale: 'Neghi la clemenza a un criminale o a un miscredente.',
    neutrale: "Fai qualcosa che non ritieni giusto pur di preservare l'equilibrio.",
    caotico:
      'Agisci in modo imprevedibile e spericolato per far volgere a tuo favore le sorti di una battaglia.',
    malvagio: 'Ignori i danni collaterali mentre stai compiendo la tua missione.',
  },

  // Manuale V1, r. 4840. **maxBonds: 3** (3 templates ↔ 3, FI-3 rispettato;
  // estremo basso del range, condiviso con il Mago).
  bondTemplates: [
    'Sospetto che _____ nasconda un segreto pericoloso. Vigilerò ogni istante su di lui!',
    '_____ ha combattuto al mio fianco e posso fidarmi di lui completamente.',
    'Rispetto le credenze di _____, ma spero che un giorno si convertirà alla retta via.',
  ],
  maxBonds: 3,

  // Manuale V1, rr. 4842-4850.
  // **Archetipo Nano (FI-1)**: `baseHpOverride: 12` invece dei 10 standard
  // del Paladino. Il calcolo derivato `hp.max` legge questo valore se
  // l'archetipo selezionato lo definisce.
  archetypes: [
    {
      id: 'umano',
      name: 'Umano',
      associatedRace: 'umano',
      description:
        'Quando dai un ordine a qualcuno e questi lo esegue senza fiatare, egli ottiene +1 al prossimo tiro.',
      grantedMoveId: 'paladino.archetipo-umano',
    },
    {
      id: 'elfo',
      name: 'Elfo',
      associatedRace: 'elfo',
      description:
        'Aggiungi la Tradizione Sacra della Natura a quelle che puoi scegliere con *Ordine Sacro*. Inoltre, quando prendi *Crociato* o *Templare*, puoi scegliere anche tra le mosse del Druido.',
      grantedMoveId: 'paladino.archetipo-elfo',
    },
    {
      id: 'nano',
      name: 'Nano',
      associatedRace: 'nano',
      description:
        'I tuoi PF massimi sono **12+Costituzione** (invece di 10+Costituzione standard del Paladino).',
      grantedMoveId: 'paladino.archetipo-nano',
      baseHpOverride: 12,
    },
    {
      id: 'halfling',
      name: 'Halfling',
      associatedRace: 'halfling',
      description:
        "Quando *discerni la realtà* riguardo una creatura, aggiungi la domanda «*L'entità ultraterrena cui sono devoto cosa vorrebbe che facessi con questa creatura?*»; +1 al prossimo tiro se agisci in accordo.",
      grantedMoveId: 'paladino.archetipo-halfling',
    },
    {
      id: 'mezzorco',
      name: "Mezz'orco",
      associatedRace: 'mezzorco',
      description:
        'Quando hai una Debilitazione, puoi subire 2 danni (*ignora armatura*) per avere +1 al prossimo tiro che richieda quella Caratteristica.',
      grantedMoveId: 'paladino.archetipo-mezzorco',
    },
  ],

  startingMoves: paladinoStartingAutoMoves.map((m) => m.id),

  // **FI-82 (2026-05-24)**: SSOT shift della scelta Ordine. La scelta
  // «Ordine Sacro / Sovrannaturale» (manuale V1 r. 4946) vive ora
  // direttamente su `paladino.faro-di-devozione.grantsMoveChoice`
  // (kind: 'from-list'). Faro è auto in `startingMoves` al L1 → grant
  // attiva → UI di scelta nella sezione "Mosse concesse" del builder L1.
  // Multiclass eredita: chi prende Faro via multiclass attiva la stessa
  // grant. Niente più gruppo `ordine` qui — single source of truth sulla
  // mossa. Riferimento `paladinoStartingChoiceMoves` invariato (le 2
  // mosse esistono nel catalogo per essere referenziate dalla grant).
  startingChoiceGroups: [],

  advancedMoves2to5: paladinoAdvanced2to5Moves.map((m) => m.id),
  advancedMoves6to10: paladinoAdvanced6to10Moves.map((m) => m.id),

  // **Casteggiante condizionato a 2 livelli (D2)**:
  //  - *Ordine Sacro* + Tradizione → casting `paladino-native` (gestito in
  //    services/spellAccess.ts). 3 Tradizioni: Negromanzia, Valore, Natura
  //    (Natura richiede archetipo Elfo, FI-2 risolto via `SpellAccess.requiresArchetype`).
  //  - *Ordine Sovrannaturale* → description-only stub (FI-27, refactor
  //    nella PR Warlock).
  //
  // Il `castingMoveId` punta a `paladino.ordine-sacro` (la mossa di scelta).
  // `manaMoveId` è semantico (il Paladino-Sacro ottiene *Mana* del Chierico
  // come side-effect; coerente con il pattern del Druido che usa `druido.mana`
  // come placeholder).
  spellcasting: {
    cantripLabel: 'Orazioni',
    castingMoveId: 'paladino.ordine-sacro',
    manaMoveId: 'paladino.mana',
    tradizioni: ['negromanzia', 'valore', 'natura'],
  },

  // Manuale V1, rr. 4826-4838
  startingEquipment: {
    always: [
      {
        id: 'razioni-da-dungeon',
        name: 'Razioni da Dungeon',
        weight: 1,
        uses: 5,
        tags: ['razione'],
      },
      {
        id: 'armatura-a-scaglie',
        name: 'Armatura a Scaglie',
        weight: 3,
        armor: 2,
        tags: ['indossato', 'ingombrante'],
      },
      {
        id: 'simbolo-entita',
        name: "Simbolo dell'entità ultraterrena",
        weight: 0,
        tags: [],
        userDescriptionPrompt: 'Descrivilo a tua scelta.',
      },
    ],
    choices: [
      {
        id: 'arma-iniziale',
        prompt: "Scegli un'opzione per le tue armi",
        pickCount: 1,
        options: [
          {
            id: 'alabarda',
            label: 'Alabarda',
            items: [
              {
                id: 'alabarda',
                name: 'Alabarda',
                weight: 3,
                tags: ['lunga', 'a due mani'],
                damage: '+1 danni',
              },
            ],
          },
          {
            id: 'spada-lunga-scudo',
            label: 'Spada lunga + Scudo',
            items: [
              {
                id: 'spada-lunga',
                name: 'Spada lunga',
                weight: 1,
                tags: ['media'],
                damage: '+1 danni',
              },
              {
                id: 'scudo',
                name: 'Scudo',
                weight: 2,
                armor: 1,
                tags: [],
                description: 'Quando usato.',
              },
            ],
          },
        ],
      },
      {
        id: 'extra-iniziale',
        prompt: "Scegli un'opzione tra le seguenti",
        pickCount: 1,
        options: [
          {
            id: 'attrezzatura-da-avventuriero',
            label: 'Attrezzatura da Avventuriero',
            items: [
              {
                id: 'attrezzatura-da-avventuriero',
                name: 'Attrezzatura da Avventuriero',
                weight: 2,
                uses: 5,
                tags: [],
              },
            ],
          },
          {
            id: 'razioni-extra-pozione',
            label: 'Razioni da Dungeon + Pozione Curativa',
            items: [
              {
                id: 'razioni-da-dungeon-extra',
                name: 'Razioni da Dungeon',
                weight: 1,
                uses: 5,
                tags: ['razione'],
              },
              {
                id: 'pozione-curativa',
                name: 'Pozione Curativa',
                weight: 0,
                uses: 1,
                tags: [],
              },
            ],
          },
        ],
      },
    ],
  },
};
